import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Alert,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UsbIcon from '@mui/icons-material/Usb';
import useAuth from '../hooks/useAuth';
import { useSensorConnection } from '../contexts/SensorConnectionProvider';
import SerialClient from '../services/serialClient';
import { API_ENDPOINT } from '../services/common';
import { classifyConnectFailure } from '../services/firmware/connectClassifier';
import { createFirmwareApi } from '../services/firmware/firmwareApi';
import ConnectPrompt from '../components/sensor-manager/ConnectPrompt';
import ConnectFailedPanel from '../components/sensor-manager/ConnectFailedPanel';
import ConnectingIndicator from '../components/sensor-manager/ConnectingIndicator';
import SerialLogPanel from '../components/sensor-manager/SerialLogPanel';
import DeviceHeader from '../components/sensor-manager/DeviceHeader';
import OverviewTab from '../components/sensor-manager/OverviewTab';
import FilesTab from '../components/sensor-manager/FilesTab';
import FirmwareTab from '../components/sensor-manager/FirmwareTab';
import SettingsPanel from '../components/sensor-manager/SettingsPanel';
import RulesTab from '../components/sensor-manager/RulesTab';
import UpdateWizard from '../components/sensor-manager/update-wizard/UpdateWizard';
import UpdateAvailableBanner from '../components/sensor-manager/update-wizard/UpdateAvailableBanner';
import { prefersFahrenheit } from '../utils/temperature';
import { prefersLegacyCounts } from '../utils/particleView';

// ---------------------------------------------------------------------------

const DEFAULT_MODEL = 'PTQS1005';

// One window of a directory listing. The device streams a row per file over
// serial, so an unbounded listing is both a long stall and (on the sensor) a
// heap it cannot afford — see the LSP command in the firmware.
const FILE_PAGE_SIZE = 50;

const FOLDERS = [
  { key: 'passive', directory: '/data/passive' },
  { key: 'captures', directory: '/data/captures' },
  { key: 'logs', directory: '/data/logs' },
];

const EMPTY_FOLDER = { entries: [], total: 0, page: 0, loaded: false, loading: false };
const EMPTY_FOLDERS = { passive: EMPTY_FOLDER, captures: EMPTY_FOLDER, logs: EMPTY_FOLDER };

function folderForPath(filePath) {
  return FOLDERS.find((f) => filePath.startsWith(`${f.directory}/`));
}

function isSerialSupported() {
  return typeof navigator !== 'undefined' && !!navigator.serial;
}

// ---------------------------------------------------------------------------

export default function SensorManagerPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('ROLE_ADMIN');

  const {
    client,
    connected,
    setConnected,
    deviceInfo,
    setDeviceInfo,
    serialLog,
    adoptClient,
    disconnect: contextDisconnect,
    suspend: suspendMonitor,
    resume: resumeMonitor,
  } = useSensorConnection();

  const portRef = useRef(null);

  const firmwareApi = useMemo(() => createFirmwareApi({ apiEndpoint: API_ENDPOINT }), []);

  const [storageInfo, setStorageInfo] = useState(null);
  // One page per folder, loaded on demand — nothing is listed until the Files
  // tab is opened.
  const [folders, setFolders] = useState(EMPTY_FOLDERS);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [busyOperation, setBusyOperation] = useState(null);
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState(null);
  const [firmwareVersions, setFirmwareVersions] = useState([]);
  const [rebootState, setRebootState] = useState(null); // null | { phase, message, attempt }
  const [connectFailed, setConnectFailed] = useState(false);
  const [connectFailureReason, setConnectFailureReason] = useState('silent');
  const [connecting, setConnecting] = useState(false);
  const [connectProgress, setConnectProgress] = useState(null); // null | { attempt, maxAttempts }
  const [wizardOpen, setWizardOpen] = useState(false);

  const serialSupported = isSerialSupported();

  // Auto-scroll of the log is handled inside SerialLogPanel (scoped to its own
  // container so live output can't yank the page).

  // ---- Device name ----
  // INFO's "device" field is the literal "vpsi-sensor" on every unit (it names
  // the product, not this sensor), so the header's name has to come from the
  // operator-set value in NVS — a separate GETNAME.
  const readDeviceName = useCallback(async (client) => {
    try {
      const name = await client.getName();
      return name && name.trim() ? name.trim() : null;
    } catch (_) {
      // Firmware without the settings subsystem rejects GETNAME, and an
      // unnamed sensor is a normal state — the header falls back to its
      // generic label rather than failing the connect. The Settings tab
      // surfaces a real read failure if the operator goes looking.
      return null;
    }
  }, []);

  // Keep the header in step with a rename done on the Settings tab, instead of
  // showing the old name until the next reconnect.
  const handleNameChange = useCallback((name) => {
    setDeviceInfo((prev) => (prev ? { ...prev, name: name?.trim() || null } : prev));
  }, [setDeviceInfo]);

  // ---- Load device state after connection ----
  const loadDeviceState = useCallback(async (client) => {
    const info = await client.info();
    const name = await readDeviceName(client);
    // protocolVersion (from the HELLO handshake) is the capability signal:
    // < 2 means legacy firmware that should be updated via the ESP Web Tools
    // browser installer rather than the v2 serial OTA.
    setDeviceInfo({ ...info, name, protocolVersion: client.protocolVersion() });

    const storage = await client.free();
    setStorageInfo(storage);

    // Deliberately no file listing here. Walking all three folders used to
    // block the connect: on a card with thousands of files the device spends
    // minutes streaming rows before anything renders, and a row that arrives
    // late leaves the rest of the listing in the pipe for the next command to
    // misread. The Files tab lists what it is showing, when it is opened.
    setFolders(EMPTY_FOLDERS);

    try {
      const config = await client.config();
      setDeviceConfig(config);
    } catch (_) {
      setDeviceConfig(null);
    }

    return info;
  }, [readDeviceName]);

  // ---- Fetch latest firmware version from API ----
  const fetchLatestFirmwareVersion = useCallback(async (model) => {
    try {
      const res = await fetch(`${API_ENDPOINT}/firmware/versions?model=${model}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const versions = data.versions || [];
        setFirmwareVersions(versions);
        const stable = versions.filter((v) => !v.prerelease);
        if (stable.length > 0) {
          setLatestFirmwareVersion(stable[0].version);
        }
      }
    } catch (_) {
      // Best-effort — don't block on firmware version check
    }
  }, []);

  // Hydrate the page-local device view (files, storage, config, firmware
  // versions) for whichever live client is connected: on (re)mount when we
  // navigated back to a live connection, AND after the provider's background
  // watchdog silently reconnects by adopting a fresh client. Keyed by client
  // identity so a path that already hydrated (manual connect / page reconnect,
  // which claim the ref below) does not double-load.
  const hydratedClientRef = useRef(null);
  useEffect(() => {
    if (!connected || !client?.isConnected?.()) return;
    if (hydratedClientRef.current === client) return;
    hydratedClientRef.current = client;
    loadDeviceState(client)
      .then((info) => fetchLatestFirmwareVersion(info?.model || DEFAULT_MODEL))
      .catch(() => {});
  }, [connected, client, loadDeviceState, fetchLatestFirmwareVersion]);

  // ---- Connect ----
  // Guarded so overlapping connect attempts (e.g. a double-click, or a
  // post-flash reconnect racing a manual click) can never both drive
  // client.connect() at once.
  const connectingRef = useRef(false);
  const connectWithPort = useCallback(async (port) => {
    if (connectingRef.current || client?.isConnected?.()) return;
    connectingRef.current = true;
    setConnecting(true);
    setConnectProgress(null);
    try {
      portRef.current = port;
      await client.connect(port, (attempt, maxAttempts) =>
        setConnectProgress({ attempt, maxAttempts })
      );
      hydratedClientRef.current = client; // claim: we load below, so the hydrate effect skips
      setConnected(true);
      const info = await loadDeviceState(client);
      await fetchLatestFirmwareVersion(info?.model || DEFAULT_MODEL);
    } finally {
      connectingRef.current = false;
      setConnecting(false);
      setConnectProgress(null);
    }
  }, [client, loadDeviceState, fetchLatestFirmwareVersion, setConnected]);

  const handleConnect = useCallback(async () => {
    try {
      setError(null);
      setConnectFailed(false);
      setWizardOpen(false);
      const port = await navigator.serial.requestPort();
      await connectWithPort(port);
    } catch (err) {
      const reason = classifyConnectFailure(err);
      if (reason === 'no-port') { return; }   // user cancelled — show nothing
      setConnectFailureReason(reason);
      setConnectFailed(true);
      setConnected(false);
      firmwareApi.reportOutcome({ outcome: 'connect-failed', reason, model: DEFAULT_MODEL });
    }
  }, [connectWithPort, firmwareApi]);

  // The Sensor Manager never connects on its own — connecting always requires an
  // explicit Connect click (handleConnect above). There is deliberately no
  // mount-time "zero-click" reconnect. Crash-recovery reconnect after an
  // unexpected disconnect is owned by the provider's armed watchdog
  // (autoReconnectEnabled), and post-flash reconnect is driven explicitly by the
  // flash flow — neither runs on page load.

  // ---- Disconnect ----
  const handleDisconnect = useCallback(async () => {
    await contextDisconnect();
    setStorageInfo(null);
    setFolders(EMPTY_FOLDERS);
    setDeviceConfig(null);
    setLatestFirmwareVersion(null);
    setActiveTab(0);
  }, [contextDisconnect]);

  // ---- Download ----
  const handleDownload = useCallback(async (filePath) => {
    try {
      setDownloading(filePath);
      setBusyOperation('downloading');
      const data = await client.readFile(filePath);

      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloading(null);
      setBusyOperation(null);
    }
  }, [client]);

  // ---- Delete ----
  const handleDelete = useCallback(async (filePath) => {
    try {
      await client.deleteFile(filePath);
      const folder = folderForPath(filePath);
      // Everything after the deleted file shifts up a slot, so the window has
      // to come from the device again rather than be patched locally.
      if (folder) await loadFolderPageRef.current(folder.key, foldersRef.current[folder.key].page);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }, [client]);

  // ---- Refresh the folder on screen ----
  const handleRefreshFiles = useCallback(async (key) => {
    try {
      setBusyOperation('refreshing');
      await loadFolderPageRef.current(key, foldersRef.current[key]?.page || 0);
      const storage = await client.free();
      setStorageInfo(storage);
    } catch (err) {
      setError(err.message || 'Failed to refresh files');
    } finally {
      setBusyOperation(null);
    }
  }, [client]);

  // ---- Files tab: one folder, one page, on demand ----
  const loadFolderPage = useCallback(async (key, page) => {
    const folder = FOLDERS.find((f) => f.key === key);
    if (!folder || !client?.isConnected?.()) return;

    setFolders((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));
    try {
      const { files, total } = await client.listFilesNewestFirst(
        folder.directory,
        page,
        FILE_PAGE_SIZE
      );
      setFolders((prev) => ({
        ...prev,
        [key]: { entries: files, total, page, loaded: true, loading: false },
      }));
    } catch (err) {
      setFolders((prev) => ({
        ...prev,
        [key]: { ...EMPTY_FOLDER, loaded: true },
      }));
      // A folder the firmware doesn't support (older builds reject /data/logs
      // with ERR:BADCMD) is empty, not an error.
      if (err.code !== 'BADCMD') {
        setError(`Couldn't list ${folder.directory}: ${err.message}`);
      }
    }
  }, [client]);

  // FilesTab reports the folder it is showing on every mount and selection;
  // only the first report for a folder costs a listing.
  const foldersRef = useRef(folders);
  foldersRef.current = folders;
  const loadFolderPageRef = useRef(loadFolderPage);
  loadFolderPageRef.current = loadFolderPage;

  // Stable identity: FilesTab calls this from an effect, so a new function on
  // every render would re-list the folder on every render.
  const handleFolderActivate = useCallback((key) => {
    const folder = foldersRef.current[key];
    if (folder?.loaded || folder?.loading) return;
    loadFolderPageRef.current(key, 0);
  }, []);

  const handleFolderPageChange = useCallback((key, page) => {
    loadFolderPageRef.current(key, page);
  }, []);

  // ---- Reconnect using saved port ----
  const reconnectWithPort = useCallback(async (port) => {
    const newClient = new SerialClient();
    await newClient.connect(port);
    adoptClient(newClient);
    await loadDeviceState(newClient);
    hydratedClientRef.current = newClient; // claim: loaded above, so the hydrate effect skips
    setConnected(true);
  }, [adoptClient, loadDeviceState, setConnected]);

  // ---- Try to connect to a port (connect() handles open internally) ----
  const tryConnectPort = useCallback(async (port) => {
    portRef.current = port;
    await reconnectWithPort(port);
  }, [reconnectWithPort]);

  // ---- Manual reconnect (from retry button — uses requestPort with user gesture) ----
  const handleReconnect = useCallback(async () => {
    setRebootState((prev) => ({
      ...prev,
      phase: 'reconnecting',
      message: 'Reconnecting...',
    }));
    try {
      await client.forceClose().catch(() => {});
      portRef.current = null;

      const port = await navigator.serial.requestPort();
      await tryConnectPort(port);
      setRebootState(null);
      setBusyOperation(null);
    } catch (_) {
      setRebootState({
        phase: 'failed',
        message: 'Could not reconnect to sensor.',
      });
    }
  }, [client, tryConnectPort]);

  // ---- Auto-reconnect after reboot (no user gesture — uses getPorts) ----
  const autoReconnect = useCallback(async () => {
    const MAX_RETRIES = 8;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      setRebootState({
        phase: 'reconnecting',
        message: `Reconnecting to sensor... (${attempt}/${MAX_RETRIES})`,
        attempt,
        maxRetries: MAX_RETRIES,
      });

      try {
        // getPorts() returns previously-authorized ports without user gesture
        const ports = await navigator.serial.getPorts();
        if (ports.length === 0) {
          throw new Error('No ports available');
        }

        // Try each authorized port
        for (const port of ports) {
          try {
            await tryConnectPort(port);
            setRebootState(null);
            setBusyOperation(null);
            return true;
          } catch (_) {
            try { await port.close(); } catch (__) {}
          }
        }
        throw new Error('No port responded');
      } catch (_) {
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
    }
    return false;
  }, [tryConnectPort]);

  // ---- Flash Firmware ----
  // Shared flash path: stream a binary to the device, then handle the reboot
  // and auto-reconnect. Used by both version-based and file-based flashing.
  const flashBinary = useCallback(async (binary, label, onProgress) => {
    // Stand the liveness monitor down: this path resets the device and runs its
    // own reboot + auto-reconnect, so the heartbeat/watchdog must not ping or
    // re-open the port underneath it.
    suspendMonitor();
    try {
      setError(null);
      setBusyOperation('flashing');

      await client.flashFirmware(binary, onProgress);

      // Device is rebooting — show reboot UI
      setConnected(false);
      setDeviceInfo(null);
      setRebootState({
        phase: 'rebooting',
        message: 'Firmware updated successfully! Your sensor is rebooting...',
        version: label,
      });

      // Clean up old client streams and close port properly
      // (forceClose cancels reader/writer before closing — plain port.close()
      // fails silently when streams are still locked)
      await client.forceClose();
      portRef.current = null;

      // Wait for device to reboot and USB to re-enumerate
      await new Promise((r) => setTimeout(r, 5000));

      // Auto-reconnect attempt
      const success = await autoReconnect();
      if (!success) {
        setRebootState({
          phase: 'failed',
          message: 'Could not automatically reconnect. The sensor may still be rebooting.',
        });
      }
    } catch (err) {
      setRebootState(null);
      setError(err.message || 'Firmware update failed');
      throw err;
    } finally {
      setBusyOperation(null);
      resumeMonitor();
    }
  }, [client, autoReconnect, setConnected, setDeviceInfo, suspendMonitor, resumeMonitor]);

  // Admin-only: flash an arbitrary local .bin (e.g. an unreleased build).
  const handleFlashFile = useCallback(async (file, onProgress) => {
    const binary = new Uint8Array(await file.arrayBuffer());
    await flashBinary(binary, file.name, onProgress);
  }, [flashBinary]);

  // Admin-only: download a published version from the API and flash it. Allows
  // installing an older release than what's currently on the device.
  const handleFlashVersion = useCallback(async (version, onProgress) => {
    onProgress?.('downloading', 0, 0);
    const res = await fetch(
      `${API_ENDPOINT}/firmware/download/${DEFAULT_MODEL}/v${version}.bin`,
      { credentials: 'include' }
    );
    if (!res.ok) {
      throw new Error(`Failed to download firmware v${version}: ${res.status}`);
    }
    const binary = new Uint8Array(await res.arrayBuffer());
    await flashBinary(binary, `v${version}`, onProgress);
  }, [flashBinary]);

  // Older firmware (protocol < 2) can't be updated reliably over the v2 serial
  // OTA, so route it to the ESP Web Tools browser installer (esptool-based,
  // robust). The wizard handles the USB branch automatically.
  const isOlderFirmware =
    connected && deviceInfo && deviceInfo.protocolVersion != null && deviceInfo.protocolVersion < 2;

  const handleOpenWizard = useCallback(async () => {
    // The wizard acquires and opens its own serial port; release ours first
    // so the browser can hand the same device to the installer without an
    // "already open" conflict. Suspend the monitor too, so the background
    // watchdog can't re-open the port out from under esp-web-tools.
    suspendMonitor();
    if (client && client.isConnected()) {
      await client.forceClose().catch(() => {});
    }
    portRef.current = null;
    setConnected(false);
    setWizardOpen(true);
  }, [client, setConnected, suspendMonitor]);

  const handleWizardExit = useCallback(() => {
    setWizardOpen(false);
    setConnectFailed(false);
  }, []);

  // Keep the liveness monitor suspended for as long as the update wizard is up,
  // and resume it however the wizard closes — exit, a fresh connect, or the
  // user navigating away mid-wizard (unmount). handleOpenWizard also suspends
  // imperatively to close the window before wizardOpen commits.
  //
  // NOTE: monitorSuspended is a boolean, not a ref-count, which is safe only
  // because the two suspend sources never overlap: a flash runs while
  // `connected` (FirmwareTab), the wizard renders only while `!connected`, so
  // the flash try/finally and this wizard effect can't both be active at once.
  useEffect(() => {
    if (!wizardOpen) return undefined;
    suspendMonitor();
    return () => resumeMonitor();
  }, [wizardOpen, suspendMonitor, resumeMonitor]);

  return (
    <>
      <Helmet>
        <title> VPSI | Sensor Manager </title>
      </Helmet>

      <Container>
        {rebootState ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              textAlign: 'center',
              gap: 2,
            }}
          >
            {(rebootState.phase === 'rebooting' || rebootState.phase === 'reconnecting') && (
              <CircularProgress size={48} />
            )}
            {rebootState.phase === 'failed' && (
              <ErrorOutlineIcon sx={{ fontSize: 48, color: 'warning.main' }} />
            )}

            <Typography variant="h6">
              {rebootState.phase === 'rebooting' && 'Firmware Updated'}
              {rebootState.phase === 'reconnecting' && 'Reconnecting'}
              {rebootState.phase === 'failed' && 'Reconnect Failed'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
              {rebootState.message}
            </Typography>

            {rebootState.phase === 'failed' && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<UsbIcon />}
                  onClick={handleReconnect}
                >
                  Reconnect
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                >
                  Close
                </Button>
              </Box>
            )}
          </Box>
        ) : !connected ? (
          <>
            {connecting ? (
              <ConnectingIndicator
                attempt={connectProgress?.attempt}
                maxAttempts={connectProgress?.maxAttempts}
              />
            ) : wizardOpen ? (
              <UpdateWizard model={DEFAULT_MODEL} onExit={handleWizardExit} />
            ) : connectFailed ? (
              <ConnectFailedPanel
                onRetry={() => window.location.reload()}
                onOpenWizard={handleOpenWizard}
                manifestPath={`${API_ENDPOINT}/firmware/manifest?model=${DEFAULT_MODEL}`}
                serialSupported={serialSupported}
                reason={connectFailureReason}
              />
            ) : (
              <ConnectPrompt onConnect={handleConnect} serialSupported={serialSupported} />
            )}
            {/* Serial log on the connect screen — admin only — so the HELLO
                handshake traffic is visible while diagnosing connect issues.
                (The USB-reinstall esptool flash bypasses serialClient, so it
                does not appear here.) */}
            {isAdmin && (
              <Accordion defaultExpanded sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Serial Log{serialLog.length ? ` (${serialLog.length})` : ''}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SerialLogPanel entries={serialLog} maxHeight={240} />
                </AccordionDetails>
              </Accordion>
            )}
          </>
        ) : (
          <>
            <DeviceHeader deviceInfo={deviceInfo} onDisconnect={handleDisconnect} />

            {isOlderFirmware && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={handleOpenWizard}>
                    Update now
                  </Button>
                }
              >
                This sensor is running older firmware (v{deviceInfo.firmware}). For a reliable
                update, use the browser installer.
              </Alert>
            )}

            {!isOlderFirmware && (
              <UpdateAvailableBanner
                currentVersion={deviceInfo?.firmware}
                latestVersion={latestFirmwareVersion}
                onUpdate={handleOpenWizard}
              />
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                <Tab label="Overview" />
                <Tab label="Files" />
                <Tab label="Firmware" />
                <Tab label="Settings" />
                <Tab label="Rules" disabled />
                {isAdmin && (
                  <Tab
                    label={serialLog.length ? `Serial Log (${serialLog.length})` : 'Serial Log'}
                  />
                )}
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <OverviewTab
                serialClient={client}
                deviceInfo={deviceInfo}
                storageInfo={storageInfo}
                fileCount={
                  folders.passive.loaded && folders.captures.loaded
                    ? folders.passive.total + folders.captures.total
                    : null
                }
                onTabChange={setActiveTab}
                busyOperation={busyOperation}
                latestFirmwareVersion={latestFirmwareVersion}
                useFahrenheit={prefersFahrenheit(user)}
                legacyCounts={prefersLegacyCounts(user)}
              />
            )}
            {activeTab === 1 && (
              <FilesTab
                folders={folders}
                pageSize={FILE_PAGE_SIZE}
                onActivate={handleFolderActivate}
                onPageChange={handleFolderPageChange}
                onDownload={handleDownload}
                onDelete={handleDelete}
                downloading={downloading}
                onRefresh={handleRefreshFiles}
                storageInfo={storageInfo}
                refreshing={busyOperation === 'refreshing'}
              />
            )}
            {activeTab === 2 && (
              <FirmwareTab
                deviceInfo={deviceInfo}
                onFlashFile={handleFlashFile}
                onFlashVersion={handleFlashVersion}
                availableVersions={firmwareVersions}
                isAdmin={isAdmin}
                connected={connected}
                latestFirmwareVersion={latestFirmwareVersion}
                onOpenWizard={handleOpenWizard}
              />
            )}
            {activeTab === 3 && (
              <SettingsPanel
                serialClient={client}
                onError={setError}
                onNameChange={handleNameChange}
              />
            )}
            {activeTab === 4 && <RulesTab />}
            {/* Serial log — admin only, isolated in its own tab so live
                traffic never reflows or scrolls the main views. */}
            {activeTab === 5 && isAdmin && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Serial Log
                </Typography>
                <SerialLogPanel entries={serialLog} />
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
}
